<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <RevPortal to="#skip-to-content-wrapper">
    <nav
      ref="navRef"
      :aria-label="i18n(translations.navAriaLabel)"
      class="bg-surface-default-low shadow-long fixed inset-x-0 bottom-0 z-50 flex max-h-dvh w-full flex-col rounded-t-lg p-24 md:bottom-auto md:left-16 md:top-16 md:max-h-[calc(100dvh-32px)] md:w-640 md:rounded-b-lg"
      :class="
        isOpen
          ? 'opacity-100 outline-default-hi'
          : '-translate-y-full opacity-5 focus-within:translate-y-0 focus-within:opacity-100'
      "
      @focusin="open"
      @focusout="onFocusOut"
    >
      <div
        ref="listboxRef"
        aria-describedby="accessibility-menu-help"
        :aria-activedescendant="activeOptionId"
        class="flex min-h-0 flex-1 flex-col gap-32 overflow-y-auto outline-none"
        role="listbox"
        tabindex="0"
        @keydown="onKeydown"
      >
        <template v-for="(group, groupIndex) in groups" :key="group.id">
          <RevDivider v-if="groupIndex > 0" />

          <div
            class="flex flex-col"
            role="group"
            :aria-labelledby="`${group.id}-heading`"
          >
            <div
              :id="`${group.id}-heading`"
              class="heading-3 text-static-default-hi mb-6"
            >
              {{ group.label }}
            </div>

            <ul class="list-none" :aria-describedby="`${group.id}-heading`">
              <li
                v-for="{ shortcut, index } in group.options"
                :key="shortcut.label"
              >
                <button
                  :id="optionId(index)"
                  aria-describedby="accessibility-menu-help"
                  :aria-label="getShortcutAriaLabel(shortcut)"
                  :aria-selected="index === activeIndex"
                  class="body-1-bold flex w-full items-center justify-between gap-18 rounded-sm px-10 py-18 text-left hover:bg-action-default-min-hover"
                  :class="
                    index === activeIndex ? 'bg-static-default-min-hover' : ''
                  "
                  role="option"
                  tabindex="-1"
                  type="button"
                  @click="activate(index)"
                  @focusin="activeIndex = index"
                  @mouseenter="activeIndex = index"
                >
                  <span class="flex items-center gap-16">
                    <component
                      :is="shortcut.icon"
                      class="text-static-default-low"
                      size="24"
                    />

                    <span>
                      {{ shortcut.label }}
                    </span>
                  </span>

                  <span
                    v-if="getShortcutKeys(shortcut).length > 0"
                    aria-hidden="true"
                    class="flex items-center gap-6"
                  >
                    <template
                      v-for="(key, keyIndex) in getShortcutKeys(shortcut)"
                      :key="keyIndex"
                    >
                      <span
                        v-if="keyIndex > 0"
                        class="body-2 text-static-default-low"
                      >
                        +
                      </span>
                      <kbd class="kbd">
                        {{ resolveKeyLabel(key) }}
                      </kbd>
                    </template>
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </template>
      </div>

      <RevDivider class="my-32" />

      <div
        id="accessibility-menu-help"
        class="body-2 text-static-default-low flex items-center justify-end gap-8"
      >
        {{ i18n(translations.navigationHelp) }}

        <span aria-hidden="true" class="body-1 flex items-center gap-8">
          <kbd class="kbd">▲</kbd>
          <kbd class="kbd">▼</kbd>
        </span>
      </div>
    </nav>
  </RevPortal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevDivider } from '@ds/components/Divider'
import { RevPortal } from '@ds/components/Portal'
import { useEventListener } from '@vueuse/core'

import { useTheMainContentShortcuts } from '~/components/TheMainContent/useTheMainContentShortcuts'
import {
  getShortcutAriaLabel,
  getShortcutKeys,
  resolveKeyLabel,
} from '~/composables/useKeyboardShortcuts/keyLabels'
import type { Shortcut } from '~/composables/useKeyboardShortcuts/types'
import { useKeyboardShortcuts } from '~/composables/useKeyboardShortcuts/useKeyboardShortcuts'

import translations from './A11yNavigationMenu.translations'

const { skipShortcuts, pageShortcuts } = useTheMainContentShortcuts()

const i18n = useI18n()
const navRef = useTemplateRef<HTMLElement>('navRef')
const listboxRef = useTemplateRef<HTMLElement>('listboxRef')

const isOpen = ref(false)
const activeIndex = ref(0)

let hasFirstTabBeenHandled = false

// Close the menu before executing each shortcut action.
function wrapShortcuts(shortcuts: Shortcut[]): Shortcut[] {
  return shortcuts.map((shortcut) => ({
    ...shortcut,
    action: () => {
      close()
      shortcut.action()
    },
  }))
}

const skipOptions = computed(() => wrapShortcuts(skipShortcuts.value))
const pageOptions = computed(() => wrapShortcuts(pageShortcuts.value))

const options = computed(() => [...skipOptions.value, ...pageOptions.value])

const groups = computed(() => [
  {
    id: 'accessibility-menu-section',
    label: i18n(translations.skipToASectionTitle),
    options: skipOptions.value.map((shortcut, index) => ({
      shortcut,
      index,
    })),
  },
  {
    id: 'accessibility-menu-page',
    label: i18n(translations.goToAPageTitle),
    options: pageOptions.value.map((shortcut, index) => ({
      shortcut,
      index: skipOptions.value.length + index,
    })),
  },
])

useKeyboardShortcuts(options)

function optionId(index: number): string {
  return `accessibility-option-${index}`
}

const activeOptionId = computed(() => optionId(activeIndex.value))

async function open() {
  if (isOpen.value) {
    return
  }

  activeIndex.value = 0
  isOpen.value = true
  await nextTick()
  listboxRef.value?.focus()
}

function close() {
  if (!isOpen.value) {
    return
  }

  isOpen.value = false

  if (navRef.value?.contains(document.activeElement)) {
    listboxRef.value?.blur()
  }
}

function activate(index: number) {
  options.value[index]?.action()
}

function onKeydown(event: KeyboardEvent) {
  const count = options.value.length

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()

    const offset = event.key === 'ArrowDown' ? 1 : -1

    activeIndex.value = (activeIndex.value + offset + count) % count
  } else if (event.key === 'Home') {
    event.preventDefault()
    activeIndex.value = 0
  } else if (event.key === 'End') {
    event.preventDefault()
    activeIndex.value = count - 1
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    activate(activeIndex.value)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}

function onFocusOut(event: FocusEvent) {
  const { relatedTarget } = event

  if (
    !(relatedTarget instanceof Node) ||
    !navRef.value?.contains(relatedTarget)
  ) {
    close()
  }
}

watch(activeIndex, (index) => {
  listboxRef.value
    ?.querySelector<HTMLElement>(`#${optionId(index)}`)
    ?.scrollIntoView({ block: 'nearest' })
})

if (import.meta.client) {
  // Some platforms (e.g. macOS with default keyboard navigation settings)
  // skip most elements when pressing Tab. To guarantee the menu is always
  // the first focused element on the page, the first Tab press moves focus
  // to the menu programmatically, whatever the platform settings are.
  useEventListener(document, 'keydown', (event) => {
    if (hasFirstTabBeenHandled || event.key !== 'Tab') {
      return
    }

    const hasNoModifier =
      !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey

    if (hasNoModifier && document.activeElement === document.body) {
      hasFirstTabBeenHandled = true
      event.preventDefault()
      open()
    }
  })

  useEventListener(document, 'pointerdown', (event) => {
    if (
      isOpen.value &&
      event.target instanceof Node &&
      !navRef.value?.contains(event.target)
    ) {
      close()
    }
  })
}
</script>

<style scoped>
.kbd {
  @apply min-w-[24px] rounded-xs body-1-bold px-4 py-0 bg-static-default-mid text-static-default-hi text-center;
}
</style>
